import React from 'react';
import { Box, Button, Typography, Dialog, DialogContent, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import wolfpackLogo from '../../../style/images/wolflogo.png';
import whiteSumsubLogo from '../../../style/images/whiteSumsubLogo.jpeg';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { trackEvent } from '../../../config/firebase';
import { Constant } from '../../../commom/Constants';

interface KycOnboardingPopupProps {
    open: boolean;
    onClose: () => void;
    source?: string;
}

const KycOnboardingPopup: React.FC<KycOnboardingPopupProps> = ({ open, onClose, source }) => {
    const navigate = useNavigate();

    const handleContinue = () => {
        onClose();
        if (Constant.ENV === "production") {
            trackEvent("trading_account_started", {
                source: source || "unknown_source",
            });
        }
        navigate('/createApplication');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: 'linear-gradient(145deg, #1a0e08 0%, #1c1108 35%, #110d1c 100%)',
                    borderRadius: '20px',
                    border: '1px solid rgba(242, 103, 74, 0.25)',
                    overflow: 'hidden',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
                },
            }}
        >
            <DialogContent sx={{ padding: '44px 40px 36px' }}>

                {/* Background glow blobs */}
                <Box sx={{ position: 'absolute', top: -80, right: -80, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, #f2674a14 0%, transparent 70%)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, #4a7af210 0%, transparent 70%)', pointerEvents: 'none' }} />

                {/* Close button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute', top: 14, right: 14,
                        color: '#9ca3af', width: 30, height: 30,
                        background: 'rgba(255,255,255,0.06)',
                        '&:hover': { color: '#ffffff', background: 'rgba(255,255,255,0.12)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                {/* Logo row — Sumsub → Wolfpack */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                    {/* Sumsub logo */}
                    <Box sx={{ bgcolor: '#1e1e1e', borderRadius: '12px', p: '10px 16px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={wolfpackLogo} alt="Wolfpack Logo" style={{ width: 75, height: 75 }} />
                    </Box>

                    <SwapHorizIcon />
                    {/* Wolfpack logo */}

                    <Box sx={{ bgcolor: '#1e1e1e', borderRadius: '12px', p: '10px 14px', display: 'flex', alignItems: 'center' }}>
                        <img src={whiteSumsubLogo} alt="Sumsub Logo" style={{ width: 75, height: 75 }} />
                        {/* <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 16, ml: 0.5 }}>sumsub</Typography> */}
                    </Box>

                </Box>

                {/* Badge */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        px: '16px', py: '5px', borderRadius: '100px',
                        background: 'rgba(242,103,74,0.10)', border: '1px solid rgba(242,103,74,0.30)',
                    }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f2674a' }} />
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#f4856c' }}>
                            Identity Verification
                        </Typography>
                    </Box>
                </Box>

                {/* Title */}
                <Typography sx={{ fontWeight: 700, fontSize: '22px', color: '#ffffff', textAlign: 'center', letterSpacing: '-0.03em', mb: '6px' }}>
                    Create Your Trading Account
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', mb: '22px', lineHeight: 1.7, maxWidth: 360, mx: 'auto' }}>
                    Wolfpack partners with Sumsub to verify your identity. You may receive emails directly from Sumsub during onboarding. These are legitimate and required to complete the process.
                </Typography>

                <Box sx={{
                    width: '100%', background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px',
                    p: '18px 20px', mb: '22px',
                }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6b7280', mb: '14px' }}>
                        What you'll need
                    </Typography>

                    {[
                        {
                            bg: 'rgba(242,103,74,0.12)', border: 'rgba(242,103,74,0.25)',
                            icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#f2674a" strokeWidth="1.6" /><path d="M3 9h18" stroke="#f2674a" strokeWidth="1.6" /><circle cx="7" cy="14" r="1" fill="#f2674a" /></svg>,
                            title: 'Government-issued ID', sub: "Passport, driver's license, or national ID",
                        },
                         {
                            bg: 'rgba(255,193,7,0.10)',
                            border: 'rgba(255,193,7,0.25)',
                            icon: (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="4" width="18" height="16" rx="2" stroke="#ffc107" strokeWidth="1.6" />
                                    <path d="M7 8h10M7 12h6M7 16h4" stroke="#ffc107" strokeWidth="1.6" strokeLinecap="round" />
                                </svg>
                            ),
                            title: 'Tax Identification Number',
                            sub: 'Enter the number as it appears on your government-issued ID',
                        },
                        // {
                        //     bg: 'rgba(78,223,109,0.10)', border: 'rgba(78,223,109,0.25)',
                        //     icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" stroke="#4edf6d" strokeWidth="1.6" /><path d="M9 12l2 2 4-4" stroke="#4edf6d" strokeWidth="1.6" strokeLinecap="round" /></svg>,
                        //     title: 'Secure & encrypted', sub: "Your data is protected by Sumsub's platform",
                        // },
                       
                    ].map(({ bg, border, icon, title, sub }) => (
                        <Box key={title} sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '10px', '&:last-child': { mb: 0 } }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {icon}
                            </Box>
                            <Box>
                                <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 500, lineHeight: 1.3 }}>{title}</Typography>
                                <Typography sx={{ color: '#6b7280', fontSize: '12px', lineHeight: 1.3 }}>{sub}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* CTA */}
                <Button
                    fullWidth
                    onClick={handleContinue}
                    sx={{
                        background: 'linear-gradient(90deg, #f2674a, #fcb118)',
                        color: 'black', fontWeight: 700, fontSize: '15px',
                        borderRadius: '50px', py: '13px', textTransform: 'none',
                        '&:hover': { background: 'linear-gradient(90deg, #e05540, #eaa010)', opacity: 0.95 },
                    }}
                >
                    Continue to Verification
                </Button>

                <Typography sx={{ fontSize: '12px', color: '#ccc', textAlign: 'center', mt: '12px' }}>
                    Your data is protected by Sumsub's platform
                </Typography>
            </DialogContent>
        </Dialog>
    );
};

export default KycOnboardingPopup;